import React from 'react';
import { ChevronDown } from 'lucide-react';
import ChannelToggles from './ChannelToggles';
import { NotificationSetting } from './notificationConstants';

interface NotificationCardProps {
  setting: NotificationSetting;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onToggleSms: (enabled: boolean) => void;
  onToggleEmail: (enabled: boolean) => void;
  onTimingChange?: (minutes: number) => void;
  timingOptions?: { value: number; label: string }[];
  timingLabel?: string;
  showTiming?: boolean;
  children?: React.ReactNode;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  setting,
  expanded = false,
  onToggleExpand,
  onToggleEnabled,
  onToggleSms,
  onToggleEmail,
  onTimingChange,
  timingOptions,
  timingLabel = 'Send timing',
  showTiming = false,
  children,
}) => {
  return (
    <div className={`border rounded-lg transition-colors ${setting.enabled ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">{setting.label}</h4>
              {!setting.enabled && (
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Disabled</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => onToggleEnabled(!setting.enabled)}
              className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors cursor-pointer ${
                setting.enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block w-5 h-5 bg-white rounded-full transition-transform ${
                  setting.enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {setting.enabled && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <ChannelToggles
                  smsEnabled={setting.sms_enabled}
                  emailEnabled={setting.email_enabled}
                  onSmsChange={onToggleSms}
                  onEmailChange={onToggleEmail}
                />
              </div>
              <div className="flex items-center gap-2">
                {showTiming && timingOptions && onTimingChange && (
                  <select
                    value={setting.timing_offset_minutes}
                    onChange={(e) => onTimingChange(parseInt(e.target.value))}
                    className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {timingOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
                {children && onToggleExpand && (
                  <button
                    type="button"
                    onClick={onToggleExpand}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    aria-label={expanded ? 'Collapse details' : 'Expand details'}
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            </div>
            {children && expanded && <div className="mt-4">{children}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCard;
